import CheckListField from '$tablet-app/src/app/components/CalendarItemForm/common/CheckListField';
import StartEndField from '$tablet-app/src/app/components/CalendarItemForm/common/StartEndField';
import Card from '$tablet-app/src/app/components/Card';
import Chip from '$tablet-app/src/app/components/Chip';
import {
  FloatingActions,
  useFloatingActionControl,
} from '$tablet-app/src/app/components/FloatingActions';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { KeyboardAvoid } from '$tablet-app/src/app/components/KeyboardAvoid';
import Row from '$tablet-app/src/app/components/Row';
import { ScreenSaveButton } from '$tablet-app/src/app/components/ScreenSaveButton';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import TextInput from '$tablet-app/src/app/components/TextInput';
import { Weekdays } from '$tablet-app/src/app/components/Weekdays/Weekdays';
import { EmojiInputField } from '$tablet-app/src/app/forms/components/EmojiInputField';
import { FormCollapsableSection } from '$tablet-app/src/app/forms/components/FormCollapsableSection';
import FormColorSection from '$tablet-app/src/app/forms/components/FormColorSection/FormColorSection';
import { FormRowIcon } from '$tablet-app/src/app/forms/components/FormRowIcon';
import { spacing } from '$tablet-app/src/app/theme/spacing';
import { CalendarItemType } from '@akin-home/amplify';
import { Button } from '@akin-home/buttons/native';
import {
  CalendarItemReminderType,
  getDefaultReminders,
  getReminderText,
} from '@akin-home/calendar-reminders';
import {
  CalendarItemUnion,
  getCalendarItemBackgroundColor,
} from '@akin-home/calendar/combined';
import { HasChecklist } from '@akin-home/checklists';
import { LargeInput } from '@akin-home/forms/native';
import {
  clearRecurrenceAttributes,
  getRecurrenceMode,
  getRecurrenceModePluralText,
  getRecurrenceModeText,
  makeDailyRecurrence,
  makeMonthlyRecurrence,
  makeWeeklyRecurrence,
  RecurrenceMode,
  setRecurrenceDays,
  setRecurrenceInterval,
} from '@akin-home/recurrence';
import { isWebBuild } from '@akin-home/utils/native';
import { addHours } from 'date-fns';
import React, { FC, useEffect, useRef } from 'react';
import {
  TextInput as ReactTextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated from 'react-native-reanimated';
import styled from 'styled-components/native';
import { convertItemType } from '../../common/actions';
import CalendarItemTypeSelector from '../CalendarItemTypeSelector';
import {
  CalendarItemFormState,
  CalenderItemFormDispatch,
  CalendarItemFormCollapsableSection as CollapsableSection,
} from './useCalendarItemForm';

export type CalendarItemFormFocusableFields = 'title' | 'checklists' | 'notes';

interface Props {
  state: CalendarItemFormState;
  origin?: CalendarItemUnion;
  dispatch: CalenderItemFormDispatch;
  onPressSave(): void;
  onPressBack(): void;
  focusedField?: CalendarItemFormFocusableFields;
}

const CalendarItemForm: FC<Props> = (props) => {
  const {
    state: { item, mode, sections },
    origin,
    dispatch,
  } = props;

  const { width } = useWindowDimensions();

  const fieldRefs = useRef<{
    [x in CalendarItemFormFocusableFields]: React.RefObject<ReactTextInput>;
  }>({
    title: React.createRef(),
    checklists: React.createRef(),
    notes: React.createRef(),
  });

  useEffect(() => {
    if (props.focusedField && fieldRefs.current[props.focusedField]) {
      fieldRefs.current[props.focusedField]?.current?.focus?.();
    }
  }, [props.focusedField]);

  const { scrollHandler, floating_action_styles } = useFloatingActionControl();

  if (!item) {
    return <FullScreenLoader />;
  }

  const onChange = (item: Partial<CalendarItemUnion>) => {
    dispatch({ type: 'update_item', payload: { item } });
  };

  const onChangeField = (attr: string) => (value: string | Date) => {
    if (attr === 'start' && typeof value !== 'string' && 'end' in item) {
      onChange({
        start: value,
        end: addHours(value, 1),
      });
    } else {
      onChange({ [attr]: value });
    }
  };

  // Removed due to change of business requirements. Kept for posterity
  const onChangeType = (type: any) =>
    dispatch({
      type: 'set_item',
      payload: { item: convertItemType(item, type) },
    });

  const toggleSection = (section: CollapsableSection) =>
    dispatch({ type: 'toggle_section', payload: { section } });

  /***********************
   * Recurrence Handlers *
   ***********************/
  const recurrence_mode = getRecurrenceMode<typeof item>(item);

  const onPressNoRecurrence = () =>
    onChange(
      clearRecurrenceAttributes<typeof item>(item) as Partial<CalendarItemUnion>
    );

  const onPressDailyRecurrence = () =>
    onChange({
      recurrence: makeDailyRecurrence(item.recurrence),
    });

  const onPressWeeklyRecurrence = () =>
    onChange({
      recurrence: makeWeeklyRecurrence(item.recurrence),
    });

  const onPressMonthlyRecurrence = () =>
    onChange({
      recurrence: makeMonthlyRecurrence(item.recurrence),
    });

  const onChangeRecurrenceDays = (byweekday: number[]) => {
    onChange({
      recurrence: setRecurrenceDays({
        recurrence: item.recurrence,
        byweekday,
      }),
    });
  };

  const onChangeRecurrenceInterval = (interval: string) => {
    /** Add try catch since could fail on parseInt */
    try {
      onChange({
        recurrence: setRecurrenceInterval(
          item.recurrence,
          interval.length ? parseInt(interval) : undefined
        ),
      });
    } catch (ex) {
      return;
    }
  };

  /*********************
   * Reminder Handlers *
   *********************/

  const reminders =
    item.type !== CalendarItemType.Meal && item.reminders
      ? item.reminders
      : getDefaultReminders(item.type);

  // For now, we only focus on the first reminder. In the future we may extend this.
  const reminder = reminders?.[0];

  const onPressReminder = (reminderType: CalendarItemReminderType | null) => {
    onChange({
      reminders:
        reminderType !== null ? [{ type: reminderType, secondsOffset: 0 }] : [],
    });
  };

  /****************
   * Tag Handlers *
   ****************/
  // Removed due to change of business requirements. Kept for posterity
  // const selectedTags = (item as unknown as Event)?.tags ?? {};

  // const onPressTag = (tag: CalendarItemTag) => {
  //   if (selectedTags[tag.name]) {
  //     // Create a shallow copy of selected Tags without the tag.name field
  //     const { [tag.name]: _, ...newTags } = selectedTags;
  //     onChange({
  //       tags: newTags,
  //     });
  //   } else {
  //     // Create a fresh copy of the tag, removing the data field.
  //     const { data: _, ...tagCopy } = tag;
  //     const newTags = { ...selectedTags, [tag.name]: tagCopy };
  //     onChange({
  //       tags: newTags,
  //     });
  //   }
  // };

  const isSmall = width < 400;

  return (
    <Screen>
      <Header>
        <Button
          variant="secondary"
          size="large"
          icon="arrow-left"
          onPress={props.onPressBack}
        />
        <Text variant="h2" weight="semibold">
          {mode === 'create' ? 'New event' : 'Change event'}
        </Text>
      </Header>

      <KeyboardAvoid behavior="height">
        <Body onScroll={scrollHandler} scrollEventThrottle={16}>
          <BodyContent>
            <FormCard>
              <TopRow isSmall={isSmall}>
                <EmojiInputField
                  emoji={item.emoji || '📅'}
                  onChange={onChangeField('emoji')}
                  backgroundColor={getCalendarItemBackgroundColor(item)}
                />

                <LargeInput
                  placeholder={`${item.type} name`}
                  value={item.title || ''}
                  onChangeText={onChangeField('title')}
                  autoFocus={props.focusedField === 'title'}
                  ref={fieldRefs.current.title}
                  style={{ width: '100%' }}
                />
              </TopRow>

              {/**
               * This is removed due to business requirements where we no longer want the choice between Event, Alert and Alarm to be present on the CalendarForm.
               * This is left here for posterity.
               * This can be cleaned up later when the Event,Alert,Alarm choice has been gone for long enough that we are certain we do not want it back.
               */}
              {mode === 'create' ? (
                <>
                  <Spacer height="1px" color="lightgrey" width="100%" />
                  <CalendarItemTypeSelector
                    value={item.type}
                    onChange={onChangeType}
                  />
                </>
              ) : null}

              {'end' in item ? (
                <StartEndField
                  start={item.start}
                  onStartChange={onChangeField('start')}
                  end={item.end}
                  onEndChange={onChangeField('end')}
                />
              ) : (
                <StartEndField
                  start={item.start}
                  onStartChange={onChangeField('start')}
                />
              )}

              <FormCollapsableSection
                onPress={toggleSection}
                hide_chevron={!!origin}
                expanded={sections[CollapsableSection.REPEAT]}
                section={CollapsableSection.REPEAT}
                left={
                  <Row alignItems="center">
                    <FormRowIcon icon="calendar" />
                    <Text>Repeat</Text>
                  </Row>
                }
                right={
                  <Text>
                    {getRecurrenceModeText(
                      origin?.recurrence || item.recurrence
                    )}
                  </Text>
                }
                isSmall={isSmall}
              >
                {!origin ? (
                  <>
                    <RecurrenceChips>
                      <Wrap width={width}>
                        <RecurrenceChip
                          chipVariant={!recurrence_mode ? 'active' : 'grey'}
                          onPress={onPressNoRecurrence}
                          width={width}
                        >
                          No repeat
                        </RecurrenceChip>

                        <RecurrenceChip
                          chipVariant={
                            recurrence_mode === RecurrenceMode.DAILY
                              ? 'active'
                              : 'grey'
                          }
                          onPress={onPressDailyRecurrence}
                          width={width}
                        >
                          Daily
                        </RecurrenceChip>
                      </Wrap>

                      <Wrap width={width}>
                        <RecurrenceChip
                          chipVariant={
                            recurrence_mode === RecurrenceMode.WEEKLY
                              ? 'active'
                              : 'grey'
                          }
                          onPress={onPressWeeklyRecurrence}
                          width={width}
                        >
                          Weekly
                        </RecurrenceChip>

                        <RecurrenceChip
                          chipVariant={
                            recurrence_mode === RecurrenceMode.MONTHLY
                              ? 'active'
                              : 'grey'
                          }
                          onPress={onPressMonthlyRecurrence}
                          width={width}
                        >
                          Monthly
                        </RecurrenceChip>
                      </Wrap>
                    </RecurrenceChips>

                    {recurrence_mode === RecurrenceMode.DAILY ? (
                      <>
                        <Spacer height="8px" />
                        <Weekdays
                          selected={item.recurrence?.byweekday}
                          onChangeIDs={onChangeRecurrenceDays}
                        />
                      </>
                    ) : null}

                    {showRecurrenceIntervalInput(recurrence_mode) ? (
                      <>
                        <Spacer height="16px" />
                        <InputRow>
                          <Text>Every</Text>
                          <StyledTextInput
                            value={`${item.recurrence?.interval || ''}`}
                            keyboardType="numeric"
                            onChangeText={onChangeRecurrenceInterval}
                          />
                          <Text>
                            {getRecurrenceModePluralText(recurrence_mode)}
                          </Text>
                        </InputRow>
                      </>
                    ) : null}
                  </>
                ) : null}
              </FormCollapsableSection>

              {/* Business requirement to remove tags as it was not being used. Can be added back later when a use shows up.s
              {item?.type === CalendarItemType.Event ? (
                <>
                  <Spacer height="1px" color="lightgrey" width="100%" />
                  <FormCollapsableSection
                    onPress={toggleSection}
                    hide_chevron={!!origin}
                    expanded={sections[CollapsableSection.TAGS]}
                    section={CollapsableSection.TAGS}
                    left={
                      <Row alignItems="center">
                        <FormRowIcon icon="tags" />
                        <Text>Tags</Text>
                      </Row>
                    }
                    right={<Text>{getTagsText(selectedTags)}</Text>}
                  >
                    {!origin ? (
                      <TagChips>
                        {Object.values(AVAILABLE_TAGS).map((tag) => {
                          return tag ? (
                            <TagChip
                              chipVariant={
                                selectedTags[tag.name] ? 'active' : 'grey'
                              }
                              onPress={() => onPressTag(tag)}
                              key={tag.name}
                            >
                              {tag.displayName}
                            </TagChip>
                          ) : null;
                        })}
                      </TagChips>
                    ) : null}
                  </FormCollapsableSection>
                </>
              ) : null}
              */}

              <Spacer height="1px" color="lightgrey" width="100%" />

              <FormCollapsableSection
                onPress={toggleSection}
                hide_chevron={!!origin}
                expanded={sections[CollapsableSection.REMINDER]}
                section={CollapsableSection.REMINDER}
                left={
                  <Row alignItems="center">
                    <FormRowIcon icon="bell" />
                    <Text>Reminder</Text>
                  </Row>
                }
                right={<Text>{getReminderText(reminder)}</Text>}
                isSmall={isSmall}
              >
                {!origin ? (
                  <ReminderChips>
                    <ReminderChip
                      chipVariant={!reminder ? 'active' : 'grey'}
                      onPress={() => onPressReminder(null)}
                      width={width}
                    >
                      No Reminder
                    </ReminderChip>

                    <ReminderChip
                      chipVariant={
                        reminder?.type === CalendarItemReminderType.Notification
                          ? 'active'
                          : 'grey'
                      }
                      onPress={() =>
                        onPressReminder(CalendarItemReminderType.Notification)
                      }
                      width={width}
                    >
                      Notification Reminder
                    </ReminderChip>

                    <ReminderChip
                      chipVariant={
                        reminder?.type === CalendarItemReminderType.Alarm
                          ? 'active'
                          : 'grey'
                      }
                      onPress={() =>
                        onPressReminder(CalendarItemReminderType.Alarm)
                      }
                      width={width}
                    >
                      Alarm Reminder
                    </ReminderChip>
                  </ReminderChips>
                ) : null}
              </FormCollapsableSection>
            </FormCard>

            <FormCard>
              <FormColorSection
                expanded={sections[CollapsableSection.COLOUR]}
                section={CollapsableSection.COLOUR}
                value={getCalendarItemBackgroundColor(item)}
                onPress={toggleSection}
                onChange={onChangeField('color')}
              />

              <Spacer height="1px" color="lightgrey" />

              {/* TODO Invitation Section */}

              {'checklist' in item ? (
                <Row>
                  <View>
                    <Spacer height="16px" />
                    <FormRowIcon icon="tasks" />
                  </View>

                  <FlexGrow>
                    <CheckListField
                      placeholder="What do you need to do or bring?"
                      item={item as HasChecklist}
                      onChange={onChange}
                      autoFocus={props.focusedField === 'checklists'}
                      textInputRef={fieldRefs.current.checklists}
                    />
                  </FlexGrow>
                </Row>
              ) : null}

              <Spacer height="1px" color="lightgrey" />

              <Row>
                <View>
                  <Spacer height="16px" />
                  <FormRowIcon icon="sticky-note" />
                </View>

                <FlexGrow>
                  <TextInput
                    placeholder="Notes..."
                    value={item.notes || ''}
                    onChangeText={onChangeField('notes')}
                    autoFocus={props.focusedField === 'notes'}
                    ref={fieldRefs.current.notes}
                    style={{ height: 'auto' }}
                    multiline
                    numberOfLines={isWebBuild() ? 4 : undefined}
                  />
                </FlexGrow>
              </Row>
            </FormCard>
          </BodyContent>
        </Body>
      </KeyboardAvoid>

      <FloatingActions style={floating_action_styles}>
        <ScreenSaveButton onPress={props.onPressSave} />
      </FloatingActions>
    </Screen>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const FlexGrow = styled.View`
  flex: 1;
`;

const Body = styled(Animated.ScrollView)`
  flex: 1;
  padding-bottom: 30px;
`;

const BodyContent = styled.View`
  padding: 0 22px 100px;
  gap: 32px;
`;

const RecurrenceChips = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 22px;

  justify-content: space-evenly;
`;

const InputRow = styled(Row)`
  max-width: 100%;
  gap: ${spacing[5]};
  align-items: center;
`;

const StyledTextInput = styled(TextInput)`
  flex: 1;
  min-width: 50px;
`;

// Removed due to business requirements. Kept for posterity.
// const TagChips = styled.View`
//   flex-direction: row;
//   gap: 22px;
//   justify-content: flex-start;
// `;
// const TagChip = styled(Chip)`
//   text-align: center;
//   flex: 1;
//   max-width: 250px;
// `;

const ReminderChips = RecurrenceChips;

const Header = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 24px 20px;
  gap: 20px;
`;

const FormCard = styled(Card)`
  padding: 18px;
  gap: 20px;
`;

const showRecurrenceIntervalInput = (mode?: RecurrenceMode) =>
  mode && [RecurrenceMode.WEEKLY, RecurrenceMode.MONTHLY].includes(mode);

// Removed due to business requirements. Kept for posterity.
// const getTagsText = (selectedTags: TagsObject) => {
//   const numTags = Object.keys(selectedTags).length ?? 0;

//   return `${numTags} tag${numTags === 1 ? '' : 's'} selected`;
// };

const TopRow = styled.View<{ isSmall?: boolean }>`
  ${({ isSmall = false }) =>
    isSmall
      ? `flex-direction: column; justify-content: center;`
      : `flex-direction: row;`}
  gap: 20px;
  align-items: center;
`;

const RecurrenceChip = styled(Chip)<{ width?: number }>`
  text-align: center;
  justify-content: center;
  min-width: 120px;

  ${({ width = 400 }) => `width: ${(width - 146) / 4}px;`}
`;

const ReminderChip = styled(Chip)<{ width?: number }>`
  text-align: center;
  justify-content: center;
  min-width: 180px;

  ${({ width = 400 }) => `width: ${(width - 146) / 3}px;`}
`;

const Wrap = styled.View<{ width?: number }>`
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  gap: 22px;

  ${({ width = 400 }) => `max-width: ${width - 146}px;`}
`;

export default CalendarItemForm;
